import { useEffect, useId, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { ClientButton, ClientInput } from "@/components/clients";
import { clientPath, useClientBasePath } from "@/hooks/use-client-base-path";
import { useTciSession } from "@/hooks/use-tci-session";
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";
import {
  buildCreateTciCalculationPayload,
  createTciCalculation,
  getApiErrorMessage,
} from "@/lib/api/tci-calculations";
import {
  getCurrencySymbol,
  isWorkflowId,
  parsePositiveNumber,
  toRegionalSpelling,
} from "@/lib/tci";
import { sessionInputsToTrackingPayload } from "@/lib/visitor-tracking-payload";
import { cn } from "@/lib/utils";

/** Exact content width from Figma frame 378:1779. */
const REPORT_READY_WIDTH_PX = 552;

/**
 * Gate screen after the 3-step calculator (Figma 378:1779).
 * Collects EBITDA + email; unlocks live results.
 */
export function ClientReportReadyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const basePath = useClientBasePath();
  const { session, updateSession, buildTciInputs, result } = useTciSession();
  const { track, visitorSessionId } = useVisitorTracking();
  const consentId = useId();
  const [showEmailError, setShowEmailError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const workflowParam = searchParams.get("workflow") ?? "";
  const workflowId = isWorkflowId(workflowParam) ? workflowParam : null;

  useEffect(() => {
    if (!workflowId) return;
    track({
      currentStep: "report_ready",
      lastEvent: "view_report_ready",
      workflowId,
    });
  }, [workflowId, track]);

  if (!workflowId || !session || session.workflowId !== workflowId) {
    return <Navigate to={basePath} replace />;
  }

  const activeSession = session;
  const regionId = activeSession.regionId;
  const currencySymbol = getCurrencySymbol(regionId);
  const workflowQuery = `?workflow=${encodeURIComponent(workflowId)}`;
  const ebitdaOk = parsePositiveNumber(activeSession.annualEbitda) !== null;
  const emailValue = activeSession.email.trim();
  const emailHasAt = emailValue.includes("@");
  const emailFilled = emailValue.length > 0;
  const emailError =
    showEmailError && !emailHasAt
      ? "Please enter a valid email address"
      : undefined;
  const canAttemptUnlock = ebitdaOk && emailFilled && !isSaving;

  function handleBack() {
    navigate(`${clientPath(basePath, "calculator/3")}${workflowQuery}`);
  }

  async function handleUnlock() {
    if (!canAttemptUnlock) return;
    if (!emailHasAt) {
      setShowEmailError(true);
      return;
    }

    const inputs = buildTciInputs();
    if (!inputs || !result) {
      setSaveError("Unable to prepare your report. Please try again.");
      return;
    }

    setShowEmailError(false);
    setSaveError(null);
    setIsSaving(true);

    try {
      track({
        currentStep: "report_ready",
        lastEvent: "unlock_attempt",
        ...sessionInputsToTrackingPayload(activeSession),
      });
      await createTciCalculation(
        buildCreateTciCalculationPayload({
          email: activeSession.email,
          consent: activeSession.consent,
          regionId: activeSession.regionId,
          businessSector: activeSession.businessSector,
          organisationName: activeSession.organisationName.trim() || null,
          inputs,
          result,
          sessionId: visitorSessionId,
          viewMode: basePath === "/presenter" ? "presenter" : "visitor",
        }),
      );
      navigate(`${clientPath(basePath, "calculator/results")}${workflowQuery}`);
    } catch (error) {
      setSaveError(
        getApiErrorMessage(
          error,
          "Could not save your report. Please try again.",
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section
      className="mx-auto my-auto flex w-full flex-col items-start gap-[50px]"
      style={{ maxWidth: REPORT_READY_WIDTH_PX }}
    >
      <h1 className="w-full max-w-[427px] font-['Inter'] text-[28px] font-normal leading-9 tracking-[-1.2px] text-white sm:text-4xl sm:leading-[44px] sm:tracking-[-1.8px] lg:text-[48.4px] lg:leading-[56.4px] lg:tracking-[-2.82px]">
        Your <span className="text-[#38bdf8]">Custom Report </span>
        is Ready
      </h1>

      <div className="flex w-full flex-col items-center gap-10">
        <div className="flex w-full flex-col gap-2">
          <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
            Target Annual Profit (EBITDA)
          </p>
          <ClientInput
            variant="currency"
            currencySymbol={currencySymbol}
            placeholder=""
            value={activeSession.annualEbitda}
            onChange={(e) => updateSession({ annualEbitda: e.target.value })}
            aria-label="Target Annual Profit (EBITDA)"
          />
        </div>

        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-col gap-2">
            <p className="font-['Inter'] text-base font-normal leading-9 tracking-[-1.2px] text-[#f5f3ee] sm:text-[20px]">
              {toRegionalSpelling(
                "Enter your email to see your customized Report",
                regionId,
              )}
            </p>
            <ClientInput
              id="tci-email"
              variant="email"
              placeholder="Email address"
              value={activeSession.email}
              onChange={(e) => {
                updateSession({ email: e.target.value });
                if (showEmailError && e.target.value.trim().includes("@")) {
                  setShowEmailError(false);
                }
                if (saveError) setSaveError(null);
              }}
              aria-label="Email address"
              error={emailError}
            />
          </div>

          <label
            htmlFor={consentId}
            className="flex w-full cursor-pointer items-start gap-[9px] py-1"
          >
            <span className="relative mt-0.5 size-4 shrink-0">
              <input
                id={consentId}
                type="checkbox"
                checked={activeSession.consent}
                onChange={(e) => updateSession({ consent: e.target.checked })}
                className="absolute inset-0 z-10 m-0 size-4 cursor-pointer opacity-0"
              />
              <span
                aria-hidden
                className={cn(
                  "pointer-events-none flex size-4 items-center justify-center border",
                  activeSession.consent
                    ? "border-[#38bdf8] bg-[rgba(56,189,248,0.35)]"
                    : "border-[rgba(56,189,248,0.35)]",
                )}
              >
                {activeSession.consent ? (
                  <svg
                    viewBox="0 0 12 12"
                    width="10"
                    height="10"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M2 6.2 4.8 9 10 3"
                      stroke="#38bdf8"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : null}
              </span>
              <span
                aria-hidden
                className={cn(
                  "pointer-events-none absolute left-0 top-0 size-4 border-l-2 border-t-2",
                  activeSession.consent
                    ? "border-[#38bdf8]"
                    : "border-[rgba(56,189,248,0.35)]",
                )}
              />
              <span
                aria-hidden
                className={cn(
                  "pointer-events-none absolute bottom-0 right-0 size-4 border-b-2 border-r-2",
                  activeSession.consent
                    ? "border-[#38bdf8]"
                    : "border-[rgba(56,189,248,0.35)]",
                )}
              />
            </span>
            <span className="min-w-0 flex-1 font-['Inter'] text-xs font-normal leading-normal text-white">
              Yes, ENBROS may contact me at this email address with necessary
              reports and updates.
            </span>
          </label>
        </div>

        <div className="flex w-full flex-col gap-2">
          <div className="flex h-auto w-full flex-col gap-2 sm:h-[46px] sm:flex-row sm:items-center sm:gap-2">
            <ClientButton
              variant="secondary"
              type="button"
              onClick={handleBack}
              disabled={isSaving}
            >
              Back
            </ClientButton>
            <div className="flex min-w-0 flex-1 sm:justify-end">
              <ClientButton
                variant="primary"
                type="button"
                onClick={() => {
                  void handleUnlock();
                }}
                disabled={!canAttemptUnlock}
                aria-busy={isSaving}
              >
                {isSaving ? "Preparing.." : "Unlock Full Report"}
              </ClientButton>
            </div>
          </div>
          {saveError ? (
            <p
              role="alert"
              className="font-['Inter'] text-xs font-normal leading-4 tracking-[-0.2px] text-red-400 sm:text-right"
            >
              {saveError}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
